/**
 * Send action & messaging — the shared client contract.
 *
 * Mirrors `twasta_runtime_engine/messaging/model.py` (the binding and template
 * shapes, the constants) and `messaging/service.py` (what the endpoints
 * answer). The constants below are the TS half of a single source:
 * `test-script/test_send_model.py` asserts they equal the Python ones.
 *
 * The browser sends ids and a template, never content: the server re-reads
 * the binding by the action's name, the records through the engine as the
 * caller, and resolves every recipient from the template and the data.
 */

export const CHANNELS = ['email', 'whatsapp', 'sms', 'telegram'] as const
export const MODES = ['pdf', 'inline_image', 'inline_html', 'link'] as const
export const GROUPINGS = ['per_entity', 'per_record', 'single'] as const

export type ChannelKey = (typeof CHANNELS)[number]
export type DeliveryMode = (typeof MODES)[number]
export type GroupingMode = (typeof GROUPINGS)[number]
export const ATTACHABLE_KINDS = ['R', 'V', 'D', 'FILED'] as const
/** `FILED`: the copy of report `obj` FILED for the record in `repository` —
 *  the exact bytes the repository holds (e.g. the signed copy), not a render. */
export const FILED_VERSIONS = ['current', 'latest', 'signed'] as const
export const FILED_MODES = ['pdf', 'inline_image', 'link'] as const
export type AttachableKind = (typeof ATTACHABLE_KINDS)[number]
export type FiledVersion = (typeof FILED_VERSIONS)[number]
export const FILED_VERSION_LABELS: Record<FiledVersion, string> = {
  current: 'Released version', latest: 'Newest filed', signed: 'Signed copy',
}
export type RecipientRole = 'to' | 'cc' | 'bcc'
export type AttachPer = 'record' | 'group'

export const CHANNEL_LABELS: Record<ChannelKey, string> = {
  email: 'Email', whatsapp: 'WhatsApp', sms: 'SMS', telegram: 'Telegram',
}

export const MODE_LABELS: Record<DeliveryMode, string> = {
  pdf: 'PDF attachment', inline_image: 'Inline image', inline_html: 'Inline HTML', link: 'Secure link',
}

export const MODE_HINTS: Record<DeliveryMode, string> = {
  pdf: 'attached as a file', inline_image: 'shown in the body', inline_html: 'the report as HTML',
  link: 'expires in 14 days',
}

export const KIND_LABELS: Record<AttachableKind, string> = {
  R: 'report', V: 'visual', D: 'dashboard', FILED: 'filed document',
}

export const GROUPING_LABELS: Record<GroupingMode, string> = {
  per_entity: 'Per entity', per_record: 'Per record', single: 'One digest',
}

export interface SendAttachable {
  id: string
  kind: AttachableKind
  obj: string
  label?: string
  /** Report prompt / visual criterion ← `{column}` of the record. */
  params: Record<string, string>
  modes: DeliveryMode[]
  /** FILED only: the document repository (K) and which version to attach. */
  repository?: string
  version?: FiledVersion
}

export interface SendWhenRule {
  column: string
  op: string
  value?: unknown
}

/** `action.send` (a button) or `follow_up_action.send` (automatic). */
export interface SendActionBinding {
  enabled?: boolean
  default_template?: string
  template?: string
  templates?: string[]
  user_templates?: boolean
  channels?: ChannelKey[]
  group_by?: string[]
  grouping?: GroupingMode
  attachables?: SendAttachable[]
  lookups?: string[]
  auto_send?: boolean
  when?: SendWhenRule | null
  tenant_customizable?: boolean
  limits?: { max_records?: number; max_message_mb?: number }
  /** Server-added: the action name / follow-up id this binding belongs to. */
  action?: string
  /** Server-added: a tenant patch is applied. */
  customized?: boolean
}

export type RecipientSource =
  | { column: string }
  | { lookup: string; match: Record<string, string>; column: string; fallback_column?: string; multi?: 'first' | 'all' }
  | { typed: string }
  | { role_users: string }
  | { record_owner: true }
  | { user: string }

export interface RecipientSpec {
  role: RecipientRole
  channel: ChannelKey
  from: RecipientSource
  label?: string
}

export interface TemplateAttachment {
  ref: string
  mode: DeliveryMode | 'document'
  per: AttachPer
  filename?: string
}

/** A ProseMirror (TipTap) document — the email body. */
export interface PmDoc {
  type: 'doc'
  content?: unknown[]
}

export interface EmailBlock {
  subject: string
  body: PmDoc
  attachments: TemplateAttachment[]
}

export interface TextBlock {
  text: string
  attachments: TemplateAttachment[]
  template_name?: string
  variables?: string[]
}

export type TemplateScope = 'starter' | 'shared' | 'personal' | 'override'
export type TemplateSource = TemplateScope | 'draft'

export interface MessageTemplate {
  id: string
  name: string
  description?: string
  obj_name?: string
  scope?: TemplateScope
  owner_key?: string
  grouping?: { mode: GroupingMode; by: string[] } | null
  recipients: RecipientSpec[]
  lookups?: Record<string, { match: Record<string, string> }>
  channels: Partial<{ email: EmailBlock; whatsapp: TextBlock; sms: TextBlock; telegram: TextBlock }>
  /** Server-added on lists. */
  source?: TemplateSource
  customized?: boolean
  updated_by?: string | null
  updated_at?: string
}

export interface ChannelCaps {
  label: string
  rich: boolean
  subject: boolean
  modes: DeliveryMode[]
  template_name?: boolean
  /** The route used when the channel is not connected in IOFlow. */
  fallback?: DeliveryRoute
}

export interface ChannelState {
  key: ChannelKey
  label: string
  /** The send allows it. */
  allowed: boolean
  /** It can deliver now — connected in IOFlow, or (email) the fallback. */
  ready: boolean
  caps: ChannelCaps
  /** Which route it would use; `route_label` is what to show. */
  route?: DeliveryRoute | null
  route_label?: string
  /** Short chip text when it cannot deliver ("not connected"). */
  unavailable?: string
}

/** A channel the send offers that cannot deliver now, and why. */
export interface UnavailableChannel {
  key: ChannelKey
  label: string
  reason: 'not_connected' | 'fallback_off' | 'no_mail_server' | string
  message: string
}

export interface FieldColumn { key: string; db_name: string; heading: string; type: string }
export interface FieldToken { key: string; heading: string }

export interface SendFields {
  header: FieldColumn[]
  forms: { form_no: string; title: string; columns: FieldColumn[] }[]
  lookups: { obj: string; columns: FieldColumn[] }[]
  group: FieldToken[]
  system: FieldToken[]
}

/** `GET …/binding?obj=&action=` */
/** How a channel's messages travel: the tenant's IOFlow connector, or the
 *  host's own mail server as the EMAIL FALLBACK when IOFlow is not set up
 *  (mirrors `messaging.model.ROUTES`). */
export type DeliveryRoute = 'ioflow' | 'platform'

/** The host's own mail server offered as the email fallback (never its
 *  credentials). */
export interface PlatformEmailInfo { available: boolean; label: string; from_address: string }

export interface BindingInfo {
  /** Per channel, how it would be delivered now (absent = cannot deliver). */
  routes?: Partial<Record<ChannelKey, DeliveryRoute>>
  platform_email?: PlatformEmailInfo
  /** Plain-language notices about the routes (e.g. "Email is sent from
   *  Platform email because email is not connected in IOFlow …"). */
  delivery_notes?: string[]
  unavailable_channels?: UnavailableChannel[]
  binding: SendActionBinding
  templates: MessageTemplate[]
  channels: ChannelState[]
  channels_ready: ChannelKey[]
  setup_needed: boolean
  fields: SendFields
  can_share: boolean
  constants?: { channels: ChannelKey[]; modes: DeliveryMode[]; groupings: GroupingMode[] }
  /** Who the messages go out as (the person sending). */
  sender?: { name?: string | null; email?: string | null }
}

export interface ResolvedRecipients { to: string[]; cc: string[]; bcc: string[]; problems: string[] }

export interface PreviewGroup {
  key: string
  label: string
  count: number
  txn_ids: string[]
  recipients: Partial<Record<ChannelKey, ResolvedRecipients>>
  skipped: ChannelKey[]
}

export interface ArtefactSummary {
  filename: string
  mode: DeliveryMode
  size: number
  ref?: string
  note?: string
  url?: string
}

export interface PreviewMessage {
  subject?: string
  html?: string
  text?: string
  variables?: string[]
  template_name?: string
  attachments?: ArtefactSummary[]
  unresolved?: string[]
  sms?: { chars: number; segments: number; encoding: string }
  group_index?: number
  channel?: ChannelKey
  /** How THIS message would travel. */
  route?: DeliveryRoute
  route_label?: string
}

export interface PreviewRequest {
  obj: string
  action: string
  txn_ids: string[]
  template_id?: string
  draft?: MessageTemplate
  grouping?: GroupingMode | ''
  channels?: ChannelKey[]
  /** `{attachable id: mode}` — the preview's radios. */
  modes?: Record<string, DeliveryMode>
  group_index?: number
  channel?: ChannelKey
}

export interface PreviewResponse {
  routes?: Partial<Record<ChannelKey, DeliveryRoute>>
  platform_email?: PlatformEmailInfo
  delivery_notes?: string[]
  unavailable_channels?: UnavailableChannel[]
  binding: SendActionBinding
  template: MessageTemplate
  channels: ChannelState[]
  chosen_channels: ChannelKey[]
  grouping: { mode: GroupingMode; by: string[] }
  groups: PreviewGroup[]
  message: PreviewMessage
  warnings: string[]
  totals: { messages: number; skipped: number; records: number; groups: number; missing: number }
  daily: { cap: number; used: number; remaining: number }
  setup_needed: boolean
}

export interface SendResponse { run_key: string; queued: number; skipped: number }

export type RunStatus = 'queued' | 'expanding' | 'running' | 'done' | 'partial' | 'failed'
export type ItemStatus = 'queued' | 'sending' | 'sent' | 'failed' | 'skipped'

export interface RunSummary {
  run_key: string
  obj_name: string
  binding_id: string
  trigger: 'button' | 'follow_up' | 'retry' | string
  template_id?: string
  template_name?: string
  status: RunStatus
  counts: Partial<Record<ItemStatus | 'total', number>>
  error?: string | null
  by?: string
  created_at: string
  finished_at?: string
  channels: ChannelKey[]
  records: number
}

export interface MessageItem {
  item_key: string
  run_key: string
  group_key: string
  group_label: string
  txn_ids: string[]
  channel: ChannelKey
  recipients: Partial<ResolvedRecipients>
  subject: string
  status: ItemStatus
  attempts: number
  message_id?: string | null
  error?: string | null
  snapshot: {
    subject?: string
    text?: string
    attachments?: ArtefactSummary[]
    template?: string
    by?: string
    via?: string
    route?: DeliveryRoute
    warnings?: string[]
  }
  created_at: string
  sent_at?: string
}

export interface RunDetail { run: RunSummary; items: MessageItem[] }

export interface TenantFollowup {
  binding_id: string
  name: string
  event: string
  triggers: string[]
  action: string | null
  send: SendActionBinding
  source: 'designer' | 'tenant'
  customized: boolean
}

export interface TenantSendObject {
  obj_name: string
  title: string
  buttons: { name: string; binding: SendActionBinding; default?: SendActionBinding; customized: boolean }[]
  followups: TenantFollowup[]
  button_names: string[]
  columns: FieldColumn[]
  tenant_customizable: boolean
}

export interface ChannelConfig {
  connector: string; app_id: string; enabled: boolean; ready: boolean
  /** Use the host's own mail server while this channel is not connected in
   *  IOFlow (email only). Absent = on. */
  fallback?: boolean
}

export interface ChannelSettings {
  channels: Partial<Record<ChannelKey, ChannelConfig>>
  from_name: string
  reply_to: string
  daily_cap: number
}

export interface TenantSendState {
  objects: TenantSendObject[]
  all_objects: string[]
  channels: ChannelSettings
  ready: ChannelKey[]
  routes?: Partial<Record<ChannelKey, DeliveryRoute>>
  platform_email?: PlatformEmailInfo
}

/** `PUT …/admin/sends/{obj}/{binding_id}` body. */
export interface TenantSendPatch {
  enabled?: boolean
  event?: string
  name?: string
  send?: Partial<SendActionBinding>
}

export interface TenantTemplates {
  obj_name: string
  starters: MessageTemplate[]
  tenant: MessageTemplate[]
}

/** IOFlow connectors the platform can connect a channel to (platform only). */
export interface ConnectorOption {
  app_id: string
  name: string
  channel?: ChannelKey | string
  connected?: boolean
  /** The connector kind (e.g. smtp_email) — what the channel mapping stores. */
  connector?: string
  auth_type?: string
  /** OAuth2 and the like: connected in IOFlow's own browser flow. */
  needs_browser?: boolean
}

/** `GET` / `PUT …/admin/channels` — the settings FLAT, plus readiness. */
export interface ChannelsAdmin extends ChannelSettings {
  ready?: ChannelKey[]
  /** Per-channel status: delivering, by which route, and why not. */
  readiness?: Partial<Record<ChannelKey, { ready: boolean; detail?: string; route?: DeliveryRoute | null }>>
  routes?: Partial<Record<ChannelKey, DeliveryRoute>>
  platform_email?: PlatformEmailInfo
  /** Channels that can fall back to the platform mail server (from the
   *  engine's CHANNEL_CAPS — email today). */
  fallback_channels?: ChannelKey[]
  /** This host can connect channels (the platform's IOFlow connect). */
  can_connect?: boolean
  /** This host can (re)provision the managed IOFlow APIs + flows. */
  can_provision?: boolean
  /** The stored settings (a nested copy of the flat fields). */
  settings?: ChannelSettings
  /** Platform: connectable IOFlow messaging apps. */
  connectors?: ConnectorOption[]
  /** Platform: IOFlow could not be reached for the connector list. */
  connectors_error?: string | null
  /** Platform: the connector catalog (informational). */
  catalog?: unknown
}

export interface ConnectCredentialField {
  name: string
  label?: string
  secret?: boolean
  required?: boolean
  default?: string
}

/** `POST …/admin/channels/connect` answer. */
export type ConnectResult =
  | { ok: true; app_id: string; subscription_id?: string | null }
  | {
    ok: false
    needs_credentials?: boolean
    app_id?: string
    app_name?: string
    auth_type?: string
    needs_browser?: boolean
    fields?: ConnectCredentialField[]
    ioflow_url?: string
    error?: string
  }

export interface ProvisionResult {
  ok: boolean
  error?: string | null
  channels?: unknown
  settings?: ChannelSettings
  ready?: ChannelKey[]
}

export interface SendErrorInfo { message: string; problems: string[]; code?: string }

/** What a host gives the shared messaging UI. Every call is authorized by the
 *  host's own gate; the UI never decides rights. */
export interface MessagingTransport {
  binding(obj: string, action: string): Promise<BindingInfo>
  templates(obj: string, action: string): Promise<MessageTemplate[]>
  saveTemplate(obj: string, action: string, template: MessageTemplate,
    scope: 'personal' | 'shared' | 'override'): Promise<MessageTemplate>
  deleteTemplate(obj: string, action: string, templateId: string,
    scope: 'personal' | 'shared' | 'override'): Promise<void>
  preview(req: PreviewRequest): Promise<PreviewResponse>
  send(req: PreviewRequest): Promise<SendResponse>
  runs(obj: string, txnId?: string, limit?: number): Promise<{ runs?: RunSummary[]; items?: MessageItem[] }>
  run(obj: string, runKey: string): Promise<RunDetail>
  retry(obj: string, runKey: string): Promise<{ retried: number }>
  // ── tenant administration (func-admin) ──
  adminSends(obj?: string): Promise<TenantSendState | TenantSendObject>
  saveSend(obj: string, bindingId: string, patch: TenantSendPatch): Promise<TenantSendObject>
  resetSend(obj: string, bindingId: string): Promise<TenantSendObject>
  adminTemplates(obj: string): Promise<TenantTemplates>
  channels(): Promise<ChannelsAdmin>
  saveChannels(settings: ChannelSettings): Promise<ChannelsAdmin>
  /** The binding info of ANY send (a button OR an automatic send) — for the
   *  admin composer. */
  adminBinding(obj: string, bindingId: string): Promise<BindingInfo>
  /** Save an organization template (a starter's override, or a shared one). */
  saveAdminTemplate(obj: string, bindingId: string, scope: 'override' | 'shared',
    template: MessageTemplate): Promise<MessageTemplate>
  deleteAdminTemplate(obj: string, templateId: string, scope: 'override' | 'shared'): Promise<void>
  /** Platform only: connect a channel to an IOFlow connector. A connector
   *  that is not connected yet answers `needs_credentials` with the fields to
   *  ask for; resend with `auth_credentials`. */
  connectChannel?(body: { app_id: string; channel: ChannelKey; auth_credentials?: Record<string, string> }): Promise<ConnectResult>
  testChannel(channel: ChannelKey, address: string): Promise<{ ok: boolean; message_id?: string | null; error?: string | null }>
  /** Platform only: (re)provision the managed IOFlow APIs + flows. */
  provision?(): Promise<ProvisionResult>
  describeError?(e: unknown, fallback: string): SendErrorInfo
}

/** The server's reason for a refused call. Understands an axios-style error
 *  (`response.data.detail`), the runtime client's ApiError (`detail`/`body`)
 *  and a plain Error; `detail` may be a string or the messaging refusal
 *  `{error, code, problems}`. */
export function sendErrorInfo(e: unknown, fallback: string): SendErrorInfo {
  const any = e as {
    response?: { data?: { detail?: unknown } }
    detail?: unknown
    body?: { detail?: unknown }
    message?: string
  } | null
  const detail = any?.response?.data?.detail ?? any?.body?.detail ?? any?.detail
  if (typeof detail === 'string' && detail) return { message: detail, problems: [] }
  if (detail && typeof detail === 'object') {
    const d = detail as { error?: unknown; message?: string; code?: string; problems?: unknown }
    const msg = typeof d.error === 'string' ? d.error
      : (d.error && typeof d.error === 'object' && typeof (d.error as { message?: string }).message === 'string')
        ? (d.error as { message: string }).message
        : d.message
    return {
      message: msg || fallback,
      problems: Array.isArray(d.problems) ? d.problems.map(String) : [],
      code: typeof d.code === 'string' ? d.code : undefined,
    }
  }
  return { message: (e instanceof Error && e.message) || any?.message || fallback, problems: [] }
}

/** What a dispatched 'send' action hands the host surface to open. */
export interface SendActionRequest {
  title: string
  objName: string
  actionName: string
  binding: SendActionBinding
  txnIds: string[]
  /** Rows the bulk run could not include (with the reason). */
  excluded?: { txn_id: string; status?: string; message?: string }[]
  /** Called when the window closes; `runKey` when something was sent. */
  onClosed?: (runKey: string | null) => void
}

export function buildSendActionRequest(
  actionName: string,
  binding: SendActionBinding | undefined,
  objName: string,
  txnIds: string[],
  onClosed?: (runKey: string | null) => void,
  label?: string,
): SendActionRequest {
  const ids: string[] = []
  for (const t of txnIds || []) {
    const s = String(t ?? '').trim()
    if (s && !ids.includes(s)) ids.push(s)
  }
  return {
    title: (label || '').trim() || actionName,
    objName,
    actionName,
    binding: binding || {},
    txnIds: ids,
    onClosed,
  }
}

/** `on-action:<button>` ⇄ the button name; '' when the event is a save trigger. */
export function followupActionOf(event: string | undefined): string {
  const e = String(event || '').trim()
  return e.toLowerCase().startsWith('on-action:') ? e.slice('on-action:'.length).trim() : ''
}

export function describeEvent(event: string | undefined): string {
  const action = followupActionOf(event)
  if (action) return `After ${action}`
  const e = String(event || '').toLowerCase().replace(/^on-/, '')
  const parts = e.split('-').filter((p) => p === 'add' || p === 'edit' || p === 'delete')
  if (!parts.length) return 'Never (no trigger)'
  return 'On ' + parts.join(' or ')
}

/** A one-line summary of a recipient for chips / lists. */
export function describeRecipient(r: RecipientSpec, objLabel?: (obj: string) => string): string {
  const f = r.from as Record<string, unknown>
  if (typeof f.lookup === 'string') {
    const m = Object.entries((f.match as Record<string, string>) || {})
      .map(([k, v]) => `${k} ← ${String(v).replace(/[{}]/g, '')}`).join(', ')
    return `${objLabel ? objLabel(f.lookup) : f.lookup}.${String(f.column || '')}${m ? ` (${m})` : ''}`
  }
  if (typeof f.column === 'string') return String(f.column)
  if (typeof f.typed === 'string') return String(f.typed)
  if (typeof f.role_users === 'string') return `everyone with role ${f.role_users}`
  if (f.record_owner) return 'whoever created the record'
  if (typeof f.user === 'string') return String(f.user)
  return '—'
}

export function emptyTemplate(objName: string): MessageTemplate {
  return {
    id: '', name: '', obj_name: objName, grouping: null,
    recipients: [],
    channels: { email: { subject: '', body: { type: 'doc', content: [{ type: 'paragraph' }] }, attachments: [] } },
  }
}

export function slugId(name: string): string {
  const s = String(name || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
  return (s || 'template').slice(0, 60)
}
